/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import TQFont, { tqtypes, TQFontAggregation, TQFontFamily, TQLogLevel } from "../public/TQTypes";
import { tqconstants } from "./constants/TQConstants";
import TQInitParams from "../public/TQInitParam";
import { TQFontParser } from "./fontparser/TQFontParser";

declare global {
    interface Navigator {
        fonts: any;
    }
}

declare global {
    interface Navigator {
        fonts: any;
    }
}

export enum HashAlgo {
    SHA1 = "SHA-1",
    SHA256 = "SHA-256",
    SHA384 = "SHA-384",
    SHA512 = "SHA-512",
}

export type TagCatalogLinks = {
    tagUrl: string;
    tagAssignmentUrl: string;
    tagNamespaceUrl: string;
    tagSynonymsUrl: string;
};

export class TQUtils {
    public static reversePartArray(array: Uint8Array, begin: number, end: number) {
        const partA = array.slice(0, begin);
        const partB = array.slice(begin, end).reverse();
        const partC = array.slice(end);

        const result = new Uint8Array(partA.length + partB.length + partC.length);
        result.set(partA);
        result.set(partB, partA.length);
        result.set(partC, partB.length);
        return result;
    }

    public static async checkIfValidFontStream(contents: Uint8Array | File) {
        let isValidSFNT = false;
        let format = "";
        // Commented out following
        // format == "ttcf"
        // format == "wOFF"
        // format == "wOF2"
        // format == "typ1"

        let fontBuffer: any;
        if (contents instanceof File) {
            const arrayBuffer = (await (contents as Blob).arrayBuffer()).slice(0, 4);
            fontBuffer = new Uint8Array(arrayBuffer);
        } else {
            fontBuffer = contents;
        }

        if (fontBuffer.length >= 4) {
            format = String.fromCharCode.apply(null, fontBuffer.subarray(0, 4) as any);
            if (format == "OTTO" || format == "true" || format === String.fromCharCode(0, 1, 0, 0)) {
                isValidSFNT = true;
            }
        }
        return isValidSFNT;
    }

    public static findSystemFonts = async (remainingFonts: string[], fetchBuffer: boolean) => {
        /* Dont search system if neccessary system APIs doesnt exist
        TODO check for system font access permissions here? */
        const foundSystemFonts: TQFont[] = [];
        try {
            if ("fonts" in Navigator) {
                // const fontsIterator = navigator.fonts.query();
                // for await (const metadata of fontsIterator) {
                //   const singlefontInfo: TQFont = new TQFont(tqtypes.FontType.SYSTEM, "", "");
                //   if (psNames.indexOf(metadata.postscriptName) > -1) {
                //     console.log("Found "+ metadata.postscriptName);
                //     const index = psNames.indexOf(metadata.postscriptName);
                //     psNames.splice(index, 1);
                //     const sfntBlob = await metadata.blob();
                //     const sfntBuffer = await sfntBlob.arrayBuffer();
                //     singlefontInfo.postscriptName = metadata.postscriptName;
                //     singlefontInfo.fontBuffer = new Uint8Array(sfntBuffer);
                //     singlefontInfo.fontName = metadata.fullName;
                //     singlefontInfo.family = new TQFontFamily(metadata.family);
                //     foundSystemFonts.push(singlefontInfo);
                //   }
                //   if (psNames.length == 0) {
                //     break;
                //   }
                // }
            }
        } catch (e) {}
        return { remainingFonts, foundSystemFonts };
    };

    public static getAppropriateRegion() {
        const appLang = TQInitParams.getAppLanguage();
        if (appLang != undefined) {
            return appLang;
        }
        const chineseRegionMapper = new Map<string, tqtypes.Language>([
            ["zh-TW", tqtypes.Language.TraditionalChinese],
            ["zh-Hant", tqtypes.Language.TraditionalChinese],
        ]);
        let region: string = tqtypes.Language.Default;
        if (navigator.languages != undefined) {
            region = navigator.languages[0];
        } else {
            region = navigator.language;
        }
        // locale information
        if (region.indexOf("zh") != -1) {
            return chineseRegionMapper.get(region) || tqtypes.Language.SimplifiedChinese;
        } else {
            region = region.split("-")[0];
        }
        return TQUtils.getLanguageFromString(region);
    }

    public static getAppropriateLocale() {
        let finalLocale = tqtypes.Locale.Default;
        const region = TQUtils.getAppropriateRegion();
        const mappedValue = tqtypes.RegionToLocale.get(region);
        if (mappedValue) {
            finalLocale = mappedValue;
        } else {
            finalLocale = tqtypes.Locale.Default;
        }
        return finalLocale;
    }

    public static setDisplayFontForEachFamilyIfNeeded(aggregation: TQFontAggregation) {
        aggregation.getEntries().forEach(family => {
            const displayPsName = family.displayFont.postscriptName;
            if (displayPsName == "") {
                // If not already set
                family.displayFont = family.getEntries()[0];

                // Double check it
                family.getEntries().some((font, index) => {
                    if (font.styleName.toLowerCase().indexOf("regular") != -1) {
                        family.displayFont = font;
                        return true; // break here
                    }

                    if (font.postscriptName.toLowerCase().indexOf("regular") != -1) {
                        family.displayFont = font;
                        return true; // break here
                    }

                    if (font.fontName.toLowerCase().indexOf("regular") != -1) {
                        family.displayFont = font;
                        return true; // break here
                    }

                    if (font.styleName.toLowerCase().indexOf("book") != -1) {
                        family.displayFont = font;
                        return true; // break here
                    }

                    if (font.styleName.toLowerCase() == "r") {
                        family.displayFont = font;
                        return true; // break here
                    }
                });
            }
        });
        return aggregation;
    }

    public static getFamiliesFromFontsArrayJSON(
        familyJSON: any,
        type: tqtypes.FontType,
        userEntitlement: tqtypes.Entitlement,
        aggregation: TQFontAggregation,
        displayFontPsName: string,
        callbackEventPerFont: any,
        appEntitlement: tqtypes.AppEntitlement,
        isActiveForUser: boolean,
        filter: string[] = []
    ) {
        // check and iterate array
        for (const fontJSON of familyJSON) {
            const font: TQFont = TQUtils.createFontFromJSONData(fontJSON, type, userEntitlement);
            font.isActiveForUser = isActiveForUser;
            if (callbackEventPerFont) {
                callbackEventPerFont(font.postscriptName, fontJSON);
            }
            let toBeIncluded = true;
            const nameLowerCase = font.fontName.toLowerCase();

            // If paid app, exclude premium fonts for FREE user
            if (appEntitlement == tqtypes.AppEntitlement.PAID && userEntitlement == tqtypes.Entitlement.TRIAL && font.isPremiumFont) {
                toBeIncluded = false;
            }

            if (toBeIncluded && filter.length) {
                for (const searchItem of filter) {
                    const searchItemLowerCase = searchItem.toLowerCase();
                    if (nameLowerCase.indexOf(searchItemLowerCase) == -1) {
                        toBeIncluded = false;
                        break;
                    }
                }
            }

            if (toBeIncluded && font.familyName != "") {
                let family = aggregation.getFamilyFromName(font.familyName);
                if (family == undefined) {
                    family = new TQFontFamily(font.familyName);
                    aggregation.addFamily(font.familyName, family);
                }
                family.addFont(font.postscriptName, font);
                if (displayFontPsName != "" && displayFontPsName == font.postscriptName) {
                    family.displayFont = font;
                }
                font.family = family;
            }
        }
        // end familyJSON iteration
        return TQUtils.setDisplayFontForEachFamilyIfNeeded(aggregation);
    }

    public static getAggregationFromFonts(fonts: TQFont[], filterString: string[] = []) {
        const aggregation = new TQFontAggregation();
        for (const font of fonts) {
            let isMatched = true;
            for (const filter of filterString) {
                if (font.fontName.toLowerCase().indexOf(filter.toLowerCase()) == -1) {
                    isMatched = false;
                    break;
                }
            }
            if (isMatched) {
                let family = aggregation.getFamilyFromName(font.familyName);
                if (family == undefined) {
                    family = new TQFontFamily(font.familyName);
                    aggregation.addFamily(font.familyName, family);
                }
                family.addFont(font.postscriptName, font);
                font.family = family;
            }
        }
        return TQUtils.setDisplayFontForEachFamilyIfNeeded(aggregation);
    }

    public static getFamiliesFromBulkDBData(
        browseDBDataArray: any[],
        aggregation: TQFontAggregation,
        appEntitlement: tqtypes.AppEntitlement,
        userEntitlement: tqtypes.Entitlement,
        isActiveForUser: boolean,
        filter: string[] = []
    ) {
        if (appEntitlement == tqtypes.AppEntitlement.PAID) {
            // If paid app, exclude premium fonts for FREE user
            browseDBDataArray = browseDBDataArray.filter(entry => {
                if (userEntitlement == tqtypes.Entitlement.TRIAL && entry.premium == true) {
                    return false;
                }
                return true;
            });
        }

        for (const browseData of browseDBDataArray) {
            const font = TQUtils.createFontFromDBData(browseData);
            font.isActiveForUser = isActiveForUser;
            let toBeIncluded = true;
            if (toBeIncluded && filter.length) {
                const nameLowerCase = font.fontName.toLowerCase();
                for (const searchItem of filter) {
                    const searchItemLowerCase = searchItem.toLowerCase();
                    if (nameLowerCase.indexOf(searchItemLowerCase)) {
                        toBeIncluded = false;
                        break;
                    }
                }
            }

            if (toBeIncluded && font.familyName != "") {
                let family = aggregation.getFamilyFromName(font.familyName);
                const isDisplay = browseData["display"] as boolean;

                if (family == undefined) {
                    family = new TQFontFamily(font.familyName);
                    aggregation.addFamily(font.familyName, family);
                }
                if (isDisplay == true) {
                    family.displayFont = font;
                }
                family.addFont(font.postscriptName, font);
                font.family = family;
            }
        }
        TQUtils.setDisplayFontForEachFamilyIfNeeded(aggregation);
        return aggregation;
    }

    public static getFamiliesFromByofData(fonts: TQFont[], aggregation: TQFontAggregation, filter: string[] = []) {
        for (const font of fonts) {
            let toBeIncluded = true;
            if (toBeIncluded && filter.length) {
                const nameLowerCase = font.fontName.toLowerCase();
                for (const searchItem of filter) {
                    const searchItemLowerCase = searchItem.toLowerCase();
                    if (nameLowerCase.indexOf(searchItemLowerCase)) {
                        toBeIncluded = false;
                        break;
                    }
                }
            }

            if (toBeIncluded && font.familyName != "") {
                let family = aggregation.getFamilyFromName(font.familyName);
                if (family == undefined) {
                    family = new TQFontFamily(font.familyName);
                    aggregation.addFamily(font.familyName, family);
                }
                family.addFont(font.postscriptName, font);
                font.family = family;
            }
        }
        TQUtils.setDisplayFontForEachFamilyIfNeeded(aggregation);
        return aggregation;
    }

    public static getFontsFromByofData(byofDataArray: any[]) {
        const fonts: TQFont[] = [];
        for (const browseData of byofDataArray) {
            const font = TQUtils.createFontFromBYOFResult(browseData);
            fonts.push(font);
        }
        return fonts;
    }

    public static createFontFromDBData(fontInfoParsed: any, type: tqtypes.FontType = tqtypes.FontType.DEFAULT) {
        const region = TQUtils.getAppropriateRegion();
        const psName: string = fontInfoParsed["psName"] as string;
        const names = fontInfoParsed["familyName"] as string[];
        let familyName = "";
        const localizedNames = new Map<tqtypes.Language, string>();
        for (const name of names) {
            const nameAndRegion = name.split("##");
            const entryRegion = nameAndRegion.pop();
            if (entryRegion) {
                const script: tqtypes.Language = TQUtils.getLanguageFromString(entryRegion);
                localizedNames.set(script, nameAndRegion.join(" "));
            }
            if (entryRegion == region || (familyName.length == 0 && entryRegion == tqtypes.Language.Default)) {
                familyName = nameAndRegion.join(" ");
            }
        }
        const fontId: string = fontInfoParsed["tkId"] as string;
        const styleNames = fontInfoParsed["styleName"] as string[];
        let styleName = "";
        for (const name of styleNames) {
            const nameAndRegion = name.split("##");
            const entryRegion = nameAndRegion.pop();
            if (entryRegion) {
                const script: tqtypes.Language = TQUtils.getLanguageFromString(entryRegion);
                const familyName = localizedNames.get(script);
                if (familyName) {
                    localizedNames.set(script, familyName + " " + nameAndRegion.join(" "));
                }
            }
            if (entryRegion == region || (styleName.length == 0 && entryRegion == tqtypes.Language.Default)) {
                styleName = nameAndRegion.join(" ");
            }
        }

        const sortOrderFamily = fontInfoParsed["sortIndex"] as number;

        const singlefontInfo: TQFont = new TQFont(type, psName, fontId);
        singlefontInfo.localizedNames = localizedNames;
        if (type == tqtypes.FontType.ACTIVATED) {
            singlefontInfo.isActiveForUser = true;
        }
        singlefontInfo.fontName = familyName + " " + styleName;
        singlefontInfo.styleName = styleName;
        singlefontInfo.sortIndex = sortOrderFamily;
        singlefontInfo.familyName = familyName;

        singlefontInfo.isPremiumFont = fontInfoParsed["premium"] as boolean;
        if (singlefontInfo.isPremiumFont == false || TQInitParams.cachedEntitlement == tqtypes.Entitlement.FULL) {
            singlefontInfo.accessLevel = tqtypes.AccessLevel.READ_WRITE; // Every catalog font can be read and written for full user
        }

        const defaultLanguage: string = fontInfoParsed["lang"] as string;

        const suportedLangs: string[] = fontInfoParsed["supportedLangs"] as string[];
        for (const lang of suportedLangs) {
            singlefontInfo.addSupportedLanguage(TQUtils.getLanguageFromString(defaultLanguage));
        }
        singlefontInfo.script = TQUtils.getLanguageFromString(defaultLanguage);
        return singlefontInfo;
    }

    public static createFontFromJSONData(fontData: JSON, type: tqtypes.FontType, userEntitlement: tqtypes.Entitlement): TQFont {
        const fontInfoParsed: any = fontData;
        const fullName: string = fontInfoParsed["name"] as string;
        const psName: string = fontInfoParsed["postscript_name"] as string;
        let familyName: string = fontInfoParsed["preferred_family_name"] as string;
        if (familyName == undefined) {
            familyName = fontInfoParsed["family"]["name"] as string; // fallback
        }
        const fontId: string = fontInfoParsed["font"]["sync"]["font_id"] as string;
        const styleName: string = fontInfoParsed["preferred_subfamily_name"] as string;
        const defaultLanguage: string = fontInfoParsed["default_language"] as string;
        const sortOrderFamily = fontInfoParsed["sort_order"] as number;

        const singlefontInfo: TQFont = new TQFont(type, psName, fontId);
        if (type == tqtypes.FontType.ACTIVATED) {
            singlefontInfo.isActiveForUser = true;
        }
        singlefontInfo.fontName = fullName;
        singlefontInfo.styleName = styleName;
        singlefontInfo.sortIndex = sortOrderFamily;
        singlefontInfo.familyName = familyName;

        const suportedLangs: string[] = fontInfoParsed["font"]["sync"]["language_support"] as string[];
        for (const lang of suportedLangs) {
            singlefontInfo.addSupportedLanguage(TQUtils.getLanguageFromString(lang));
        }

        const entitlementLevelJson = fontInfoParsed["font"]["sync"]["library_availability"];
        if (entitlementLevelJson) {
            const trialEntitlement = entitlementLevelJson[tqtypes.Entitlement.TRIAL] as boolean;
            if (trialEntitlement == false) {
                singlefontInfo.isPremiumFont = true;
            } else {
                singlefontInfo.isPremiumFont = false;
            }
        }
        if (singlefontInfo.isPremiumFont == false || userEntitlement == tqtypes.Entitlement.FULL) {
            singlefontInfo.accessLevel = tqtypes.AccessLevel.READ_WRITE; // Every catalog font can be read and written for full user
        }

        singlefontInfo.script = TQUtils.getLanguageFromString(defaultLanguage);
        return singlefontInfo;
    }

    public static createFontFromFindFontResult(fontData: JSON, type: tqtypes.FontType): TQFont {
        const fontInfoParsed: any = fontData;
        const found: boolean = fontInfoParsed["found"] as boolean;
        let psName = "";
        if (fontInfoParsed["postscript_name"]) {
            psName = fontInfoParsed["postscript_name"] as string;
        }
        const fontInfo: TQFont = new TQFont(type, psName, "");
        const tkID: string = fontInfoParsed["tk_font_id"] as string;
        if (tkID) {
            fontInfo.fontId = tkID;
        }

        if (type == tqtypes.FontType.ACTIVATED) {
            fontInfo.isActiveForUser = true;
        }
        if (found) {
            const familyName: string = fontInfoParsed["preferred_family_name"] as string;
            const styleName: string = fontInfoParsed["preferred_subfamily_name"] as string;
            fontInfo.familyName = familyName;
            fontInfo.styleName = styleName;
            fontInfo.fontName = familyName + " " + styleName;

            fontInfo.isPremiumFont = false;

            // Pranay: IMO this is not perfect, user level entitlement can change in upgrade/downgrade scenarios hence client
            // should also be responded with font's own entitlement level, not only if user is at the moment entitled or not.
            // Bulk Found Fonts API should also share the font level metadata by default if possible.
            const entitlement = fontInfoParsed["entitlement"];
            if (entitlement) {
                const isEntitled = entitlement["entitled"] as boolean;
                if (isEntitled == false) {
                    fontInfo.isPremiumFont = true;
                } else {
                    fontInfo.accessLevel = tqtypes.AccessLevel.READ_WRITE; // font can be read and written
                    // TODO: Pranay collab for anonymous
                }
            }
        }
        return fontInfo;
    }

    public static createFontFromBYOFResult(fontData: JSON): TQFont {
        const fontInfoParsed: any = fontData;
        const psName: string = fontInfoParsed["postscript_name"] as string;
        const fontInfo: TQFont = new TQFont(tqtypes.FontType.BYOF, psName, "");
        const tkID: string = fontInfoParsed["file_checksum"] as string;
        const familyName: string = fontInfoParsed["family_name"] as string;
        const styleName: string = fontInfoParsed["subfamily_name"] as string;
        fontInfo.familyName = familyName;
        fontInfo.styleName = styleName;
        fontInfo.fontId = tkID;
        fontInfo.fontName = familyName + " " + styleName;
        fontInfo.isPremiumFont = false;
        fontInfo.accessLevel = tqtypes.AccessLevel.READ_WRITE;
        fontInfo.isActiveForUser = true;
        try {
            if (fontInfoParsed["created_at"]) {
                const arr = fontInfoParsed["created_at"].split(/[^0-9]/);
                const date = new Date(arr[0], arr[1] - 1, arr[2], arr[3], arr[4], arr[5]);
                fontInfo.serverTimestamp = date.getTime();
            }
        } catch (e) {
            // ..
        }
        return fontInfo;
    }

    public static parseTagCatalogResponse(response: any, locale: string): TagCatalogLinks | undefined {
        const localeMap: Map<string, TagCatalogLinks> = new Map<string, TagCatalogLinks>();
        const regsionMap: Map<string, TagCatalogLinks> = new Map<string, TagCatalogLinks>();

        let defaultLink: TagCatalogLinks | undefined = undefined;
        const links: any = response["links"];
        for (const key in links) {
            const localeLinks = links[key];
            const entryLocale = key as string;
            const link: TagCatalogLinks = {
                tagUrl: localeLinks["tags"] as string,
                tagAssignmentUrl: localeLinks["tag_assignments"] as string,
                tagNamespaceUrl: localeLinks["tag_namespaces"] as string,
                tagSynonymsUrl: localeLinks["tag_synonyms"] as string,
            };

            if (entryLocale == tqtypes.RegionToLocale.get(tqtypes.Language.Default)) {
                defaultLink = link;
            }
            localeMap.set(entryLocale, link);
            regsionMap.set(entryLocale.slice(0, 2), link);
        }
        // Now let's match a locale
        let bestEntry = localeMap.get(locale);
        if (bestEntry == undefined) {
            // No Match found in locale, let's match region

            bestEntry = regsionMap.get(locale.slice(0, 2));
            // If still undefined, go to en_US
            if (bestEntry == undefined && defaultLink) {
                bestEntry = defaultLink;
            }
        }
        return bestEntry;
    }

    public static arrayUnique(array: any[]) {
        const a = array.concat();
        for (let i = 0; i < a.length; ++i) {
            for (let j = i + 1; j < a.length; ++j) {
                if (a[i].psName === a[j].psName) {
                    a.splice(j--, 1);
                }
            }
        }
        return a;
    }

    public static mapStatusNumberToErrorCode(status: number): tqtypes.ErrorCode {
        switch (status) {
            case tqconstants.ResponseStatus.OK:
            case tqconstants.ResponseStatus.NO_CONTENT:
            case tqconstants.ResponseStatus.PARTIAL_CONTENT:

            case tqconstants.ResponseStatus.FOUND:
            case tqconstants.ResponseStatus.NOT_MODIFIED:
                return tqtypes.ErrorCode.NONE;

            case tqconstants.ResponseStatus.REDIRECTED:
            case tqconstants.ResponseStatus.USE_PROXY:
            case tqconstants.ResponseStatus.REDIRECT_TEMP:
            case tqconstants.ResponseStatus.REDIRECT_PERMANENTLY:
                return tqtypes.ErrorCode.UNKNOWN;

            case tqconstants.ResponseStatus.BAD_REQUEST:
            case tqconstants.ResponseStatus.METHOD_NOT_ALLOWED:
                return tqtypes.ErrorCode.BAD_REQUEST;

            case tqconstants.ResponseStatus.UNAUTHORIZED:
                return tqtypes.ErrorCode.AUTH_FAILURE;

            case tqconstants.ResponseStatus.FORBIDDEN:
            case tqconstants.ResponseStatus.NOT_FOUND:
                return tqtypes.ErrorCode.FORBIDDEN;

            case tqconstants.ResponseStatus.PROXY_REQUIRED:
                return tqtypes.ErrorCode.PROXY_FAILURE;

            case tqconstants.ResponseStatus.REQUEST_TIMEOUT:
                return tqtypes.ErrorCode.OFFLINE;

            // server  errors
            case tqconstants.ResponseStatus.RETRY_WITH:
            case tqconstants.ResponseStatus.SERVER_ERROR:
            case tqconstants.ResponseStatus.NOT_IMPLMENTED:
            case tqconstants.ResponseStatus.BAD_GATEWAY:
                return tqtypes.ErrorCode.UNKNOWN;

            case tqconstants.ResponseStatus.TOO_MANY_REQUESTS:
                return tqtypes.ErrorCode.TOO_MANY_REQUESTS;

            case tqconstants.ResponseStatus.SERVICE_UNAVAILABLE:
            case tqconstants.ResponseStatus.GATEWAY_TIMEOUT:
                return tqtypes.ErrorCode.SERVER_UNREACHABLE;

            case tqconstants.ResponseStatus.NETWORK_AUTH_NEEDED:
                return tqtypes.ErrorCode.AUTH_FAILURE;

            case tqconstants.ResponseStatus.NETWORK_READ_TIMEOUT:
            case tqconstants.ResponseStatus.NETWORK_CONNECT_TIMEOUT:
                return tqtypes.ErrorCode.SERVER_UNREACHABLE;
        }
        return tqtypes.ErrorCode.UNKNOWN;
    }

    public static mapLangToPrimaryScriptGroup(lang: tqtypes.Language): tqtypes.PrimaryScriptGroups {
        switch (lang) {
            case tqtypes.Language.Armenian:
            case tqtypes.Language.Belarusian:
            case tqtypes.Language.Catalan:
            case tqtypes.Language.Cherokee:
            case tqtypes.Language.Croatian:
            case tqtypes.Language.Czech:
            case tqtypes.Language.Danish:
            case tqtypes.Language.Dutch:
            case tqtypes.Language.Filipino:
            case tqtypes.Language.Finnish:
            case tqtypes.Language.French:
            case tqtypes.Language.German:
            case tqtypes.Language.Greek:
            case tqtypes.Language.Hungarian:
            case tqtypes.Language.Indonesian:
            case tqtypes.Language.Italian:
            case tqtypes.Language.Kazakh:
            case tqtypes.Language.Latvian:
            case tqtypes.Language.Macedonian:
            case tqtypes.Language.Maltese:
            case tqtypes.Language.Mandarin:
            case tqtypes.Language.Norwegian:
            case tqtypes.Language.Polish:
            case tqtypes.Language.Portuguese:
            case tqtypes.Language.Romanian:
            case tqtypes.Language.Russian:
            case tqtypes.Language.Serbian:
            case tqtypes.Language.Slovak:
            case tqtypes.Language.Slovenian:
            case tqtypes.Language.Spanish:
            case tqtypes.Language.Swedish:
            case tqtypes.Language.Turkish:
            case tqtypes.Language.Ukrainian:
            case tqtypes.Language.Vietnamese:
                return tqtypes.PrimaryScriptGroups.WESTERN;

            case tqtypes.Language.Japanese:
                return tqtypes.PrimaryScriptGroups.JAPANESE;

            case tqtypes.Language.SimplifiedChinese:
                return tqtypes.PrimaryScriptGroups.SIMPLIFIED_CHINESE;

            case tqtypes.Language.TraditionalChinese:
            case tqtypes.Language.HongKongCh:
                return tqtypes.PrimaryScriptGroups.TRAD_CHINESE;

            case tqtypes.Language.Korean:
                return tqtypes.PrimaryScriptGroups.KOREAN;

            case tqtypes.Language.Arabic:
            case tqtypes.Language.Persian:
                return tqtypes.PrimaryScriptGroups.ARABIC;

            case tqtypes.Language.Hebrew:
                return tqtypes.PrimaryScriptGroups.HEBREW;

            case tqtypes.Language.Bengali:
            case tqtypes.Language.Devanagari:
            case tqtypes.Language.Gujarati:
            case tqtypes.Language.Gurmukhi:
            case tqtypes.Language.Hindi:
            case tqtypes.Language.Kannada:
            case tqtypes.Language.Malayalam:
            case tqtypes.Language.Tamil:
            case tqtypes.Language.Telugu:
            case tqtypes.Language.Thai:
            case tqtypes.Language.Malay:
            case tqtypes.Language.Myanmar:
            case tqtypes.Language.Khmer:
            case tqtypes.Language.Sinhalese:
            case tqtypes.Language.Lao:
                return tqtypes.PrimaryScriptGroups.INDIC;

            case tqtypes.Language.Emoji:
                return tqtypes.PrimaryScriptGroups.WESTERN;

            default:
                return tqtypes.PrimaryScriptGroups.WESTERN;
        }
    }

    public static mapLangToInternalPrimaryScriptSubgroup(lang: tqtypes.Language): tqtypes.InternalPrimaryScriptSubGroups {
        switch (lang) {
            case tqtypes.Language.Armenian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_ARMENIAN;

            case tqtypes.Language.Belarusian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_BELARUSIAN;

            case tqtypes.Language.Catalan:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_CATALAN;

            case tqtypes.Language.Cherokee:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_CHEROKEE;

            case tqtypes.Language.Croatian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_CROATIAN;

            case tqtypes.Language.Czech:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_CZECH;

            case tqtypes.Language.Danish:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_DANISH;

            case tqtypes.Language.Dutch:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_DUTCH;

            case tqtypes.Language.Emoji:
                return tqtypes.InternalPrimaryScriptSubGroups.EMOJI_EMOJI;

            case tqtypes.Language.Filipino:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_FILIPINO;

            case tqtypes.Language.Finnish:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_FINNISH;

            case tqtypes.Language.French:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_FRENCH;

            case tqtypes.Language.German:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_GERMAN;

            case tqtypes.Language.Greek:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_GREEK;

            case tqtypes.Language.Hungarian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_HUNGARIAN;

            case tqtypes.Language.Indonesian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_INDONESIAN;

            case tqtypes.Language.Italian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_ITALIAN;

            case tqtypes.Language.Kazakh:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_KAZAKH;

            case tqtypes.Language.Latvian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_LATVIAN;

            case tqtypes.Language.Macedonian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_MACEDONIAN;

            case tqtypes.Language.Maltese:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_MALTESE;

            case tqtypes.Language.Mandarin:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_MANDARIN;

            case tqtypes.Language.Norwegian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_NORWEGIAN;

            case tqtypes.Language.Polish:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_POLISH;

            case tqtypes.Language.Portuguese:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_PORTUGUESE;

            case tqtypes.Language.Romanian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_ROMANIAN;

            case tqtypes.Language.Russian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_RUSSIAN;

            case tqtypes.Language.Serbian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_SERBIAN;

            case tqtypes.Language.Slovak:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_SLOVAK;

            case tqtypes.Language.Slovenian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_SLOVENIAN;

            case tqtypes.Language.Spanish:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_SPANISH;

            case tqtypes.Language.Swedish:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_SWEDISH;

            case tqtypes.Language.Turkish:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_TURKISH;

            case tqtypes.Language.Ukrainian:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_UKRAINIAN;

            case tqtypes.Language.Vietnamese:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_VIETNAMESE;

            case tqtypes.Language.Japanese:
                return tqtypes.InternalPrimaryScriptSubGroups.JAPANESE_JAPANESE;

            case tqtypes.Language.SimplifiedChinese:
                return tqtypes.InternalPrimaryScriptSubGroups.SIMPLIFIED_CHINESE_SIMPLIFIEDCHINESE;

            case tqtypes.Language.TraditionalChinese:
                return tqtypes.InternalPrimaryScriptSubGroups.TRAD_CHINESE_TRADITIONALCHINESE;

            case tqtypes.Language.HongKongCh:
                return tqtypes.InternalPrimaryScriptSubGroups.TRAD_CHINESE_HONGKONGCH;

            case tqtypes.Language.Korean:
                return tqtypes.InternalPrimaryScriptSubGroups.KOREAN_KOREAN;

            case tqtypes.Language.Arabic:
                return tqtypes.InternalPrimaryScriptSubGroups.ARABIC_ARABIC;

            case tqtypes.Language.Persian:
                return tqtypes.InternalPrimaryScriptSubGroups.ARABIC_PERSIAN;

            case tqtypes.Language.Hebrew:
                return tqtypes.InternalPrimaryScriptSubGroups.HEBREW_HEBREW;

            case tqtypes.Language.Bengali:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_BENGALI;

            case tqtypes.Language.Devanagari:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_DEVANAGARI;

            case tqtypes.Language.Gujarati:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_GUJARATI;

            case tqtypes.Language.Gurmukhi:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_GURMUKHI;

            case tqtypes.Language.Hindi:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_HINDI;

            case tqtypes.Language.Kannada:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_KANNADA;

            case tqtypes.Language.Malayalam:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_MALAYALAM;

            case tqtypes.Language.Tamil:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_TAMIL;

            case tqtypes.Language.Telugu:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_TELUGU;

            case tqtypes.Language.Thai:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_THAI;

            case tqtypes.Language.Malay:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_MALAY;

            case tqtypes.Language.Myanmar:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_MYANMAR;

            case tqtypes.Language.Khmer:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_KHMER;

            case tqtypes.Language.Sinhalese:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_SINHALESE;

            case tqtypes.Language.Lao:
                return tqtypes.InternalPrimaryScriptSubGroups.INDIC_LAO;

            default:
                return tqtypes.InternalPrimaryScriptSubGroups.WESTERN_DEFAULT;
        }
    }

    /**
     * Converts the given string into a SHA256 hex string.
     *
     * @param {string} message The message to convert.
     * @return {string} A SHA256 hex string.
     */
    public static async convertToSha256(message: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(message);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        // convert bytes to hex string
        return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    }

    /**
     * @param {"SHA-1"|"SHA-256"|"SHA-384"|"SHA-512"} algorithm https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
     * @param {string|Blob} data
     */
    public static async getHash(algorithm: HashAlgo, data: string | Blob) {
        const main = async (msgUint8: any) => {
            // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
            const hashBuffer = await crypto.subtle.digest(algorithm, msgUint8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, "0")).join(""); // convert bytes to hex string
        };
        if (data instanceof Blob) {
            const arrayBuffer = await data.arrayBuffer();
            const msgUint8 = new Uint8Array(arrayBuffer);
            return await main(msgUint8);
        }
        const encoder = new TextEncoder();
        const msgUint8 = encoder.encode(data);
        return await main(msgUint8);
    }

    public static parseETagValue(value: string) {
        const res = value.match('W/"(.*)"');
        if (res && res.length >= 1) {
            return res[1];
        }
        return value;
    }

    public static toBytesInt32(num: number) {
        const arr = new ArrayBuffer(4); // an Int32 takes 4 bytes
        const view = new DataView(arr);
        view.setUint32(0, num, false); // byteOffset = 0; litteEndian = false
        return new Uint8Array(arr);
    }

    public static copyAttributes(excludeKeys: any[], sourceObj: any, cloneObj: any) {
        for (const attribut in sourceObj) {
            if (excludeKeys.length && excludeKeys.indexOf(attribut) != -1) {
                // ignore
            } else if (typeof sourceObj[attribut] === "object" && (sourceObj[attribut] as any).clone != undefined) {
                cloneObj[attribut] = (sourceObj[attribut] as any).clone();
            } else {
                if (sourceObj[attribut] instanceof Map) {
                    cloneObj[attribut] = {
                        dataType: "Map",
                        value: Array.from((sourceObj as any)[attribut].entries()), // or with spread: value: [...value]
                    };
                } else if (sourceObj[attribut] instanceof Set) {
                    cloneObj[attribut] = {
                        dataType: "Set",
                        value: Array.from((sourceObj as any)[attribut].values()), // or with spread: value: [...value]
                    };
                } else if (cloneObj[attribut] instanceof Map) {
                    cloneObj[attribut] = new Map(sourceObj[attribut].value);
                } else if (cloneObj[attribut] instanceof Set) {
                    cloneObj[attribut] = new Set(sourceObj[attribut].value);
                } else {
                    cloneObj[attribut] = sourceObj[attribut];
                }
            }
        }
    }

    public static generateCustomOTVarName(tqFont: TQFont, axisValues: Map<string, number>) {
        // For e.g. => AcuminConcept_508.001wght_69.03wdth_7.8slnt
        try {
            let axisValuesString = tqFont.variableFamilyPrefix;
            if (axisValuesString) {
                axisValuesString = tqFont.familyName;
            }
            axisValuesString = axisValuesString.trim().replace(/\W/g, ""); // Remove NonAlphaNumerics
            const fontParser = new TQFontParser(tqFont.fontBuffer);
            const orderingMap = fontParser.getFVarOrdering();
            for (const entry of orderingMap) {
                const keyIfPresent = axisValues.get(entry[0]);
                if (keyIfPresent != undefined) {
                    axisValuesString = axisValuesString + "_";
                    const singleAxisValueString = keyIfPresent % 1 == 0 || keyIfPresent == 0 ? keyIfPresent.toFixed(0) : +keyIfPresent.toFixed(3);
                    axisValuesString = axisValuesString + singleAxisValueString + entry[0].trimRight();
                    axisValues.delete(entry[0]); // remove this processed entry
                }
            }

            for (const unprocessedEntry of axisValues) {
                axisValuesString = axisValuesString + "_";
                axisValuesString = axisValuesString + unprocessedEntry[1].toFixed(3) + unprocessedEntry[0];
            }
            return axisValuesString;
        } catch (e) {
            // ..
        }
        return undefined;
    }

    public static getAxisValuesFromPostscriptName(postscriptName: string) {
        const axisvaluesMap = new Map<string, number>();
        let axisValues = postscriptName.substring(postscriptName.indexOf("_") + 1);
        while (axisValues.length) {
            const axisValue = parseFloat(axisValues);
            const endOfFirstFloat = axisValues.match(/[a-zA-Z]+/g);
            if (endOfFirstFloat && endOfFirstFloat.length) {
                axisValues = axisValues.substring(axisValues.indexOf(endOfFirstFloat[0]));
                let axisTag = axisValues;
                if (axisValues.indexOf("_") != -1) {
                    axisTag = axisValues.substring(0, axisValues.indexOf("_"));
                }
                axisValues = axisValues.substring(axisTag.length + 1);
                axisvaluesMap.set(axisTag, axisValue);
            }
        }
        return axisvaluesMap;
    }

    public static equalMaps(m1: Map<string, number>, m2: Map<string, number>) {
        if (m1.size != m2.size) {
            return false;
        }
        for (const key of m1.keys()) {
            if (m1.get(key) != m2.get(key)) {
                return false;
            }
        }
        return true;
    }

    public static getLanguageFromString(langStr: string) {
        if (langStr == undefined || langStr == null) {
            return tqtypes.Language.Default;
        }
        let script: tqtypes.Language = langStr as tqtypes.Language;
        if (false == Object.values(tqtypes.Language).includes(script)) {
            // Check if any other way it matches
            const chineseRegionMapper = new Map<string, tqtypes.Language>([
                ["zh-CN", tqtypes.Language.SimplifiedChinese],
                ["zh-TW", tqtypes.Language.TraditionalChinese],
            ]);
            // locale information
            if (langStr.indexOf("zh") != -1) {
                script = chineseRegionMapper.get(langStr) || tqtypes.Language.SimplifiedChinese;
            } else if (langStr.indexOf("zxx-Zsye") != -1) {
                script = tqtypes.Language.Emoji;
            } else if (langStr.indexOf("nb") != -1) {
                script = tqtypes.Language.Norwegian;
            } else {
                const partScript = script.substring(0, 2) as tqtypes.Language;
                if (true == Object.values(tqtypes.Language).includes(partScript)) {
                    script = partScript;
                } else {
                    // really unknown
                    TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `An unknown script encountered = ${langStr}`);
                    script = tqtypes.Language.Default;
                }
            }
        }
        return script;
    }

    public static overrideFontScriptFromPostscriptName(font: TQFont) {
        const lowerPsName = font.postscriptName.toLocaleLowerCase();
        if (lowerPsName.indexOf("emoji") != -1) {
            font.script = tqtypes.Language.Emoji;
        } else if (lowerPsName.indexOf("khmer") != -1) {
            font.script = tqtypes.Language.Khmer;
        } else if (lowerPsName.indexOf("myanmar") != -1) {
            font.script = tqtypes.Language.Myanmar;
        } else if (lowerPsName.indexOf("sinhala") != -1) {
            font.script = tqtypes.Language.Sinhalese;
        } else if (lowerPsName.indexOf("sanslao") != -1) {
            font.script = tqtypes.Language.Lao;
        }
    }

    public static isGivenScopePresent(accessToken: string, scope: string) {
        try {
            const firstIndex = accessToken.indexOf(".");
            const secIndex = accessToken.indexOf(".", firstIndex + 1);
            const info: any = atob(accessToken.substring(firstIndex + 1, secIndex));
            return JSON.parse(info)["scope"].indexOf(scope) != -1;
        } catch (e) {
            // ...
            return false;
        }
    }

    public static isValidAccessToken(accessToken: string): boolean {
        let isAccessTokenValid = false;
        try {
            if (accessToken != "") {
                const firstIndex = accessToken.indexOf(".");
                const secIndex = accessToken.indexOf(".", firstIndex + 1);
                if (firstIndex != -1 && secIndex != -1) {
                    const info: any = atob(accessToken.substring(firstIndex + 1, secIndex));
                    const userId = JSON.parse(info)["user_id"];
                    isAccessTokenValid = userId != undefined && userId.length > 0;
                }
            }
        } catch (e) {
            // ...
        }
        return isAccessTokenValid;
    }
}

/**
 Convertor for modes
converter = {

    listing: {
      '0': '---',
      '1': '--x',
      '2': '-w-',
      '3': '-wx',
      '4': 'r--',
      '5': 'r-x',
      '6': 'rw-',
      '7': 'rwx'
    },

    modeToRWX(mode) {
      let octal = this.modeToOctal(mode);
      let rwx = this.octalToRWX(octal);
      return rwx;
    },

    modeToOctal(mode) {
      let octal = '0' + (mode & 0o777).toString(8);
      return octal;
    },

    octalToRWX(octal) {
      if (!octal) { return void 0; }
      const list = this.listing;
      let full = String((octal.length === 4) ? octal : '0' + octal);
      let a = list[String(octal).charAt(1)];
      let b = list[String(octal).charAt(2)];
      let c = list[String(octal).charAt(3)];
      return a + b + c;
    }


        // TODO: Pranay for support filePath
        // try {
        //     FS.mkdirTree("/fonts/");
        //     FS.mount(IDBFS, {}, "/fonts/");
        // } catch (e) {
        //     // ...
        // }
}
 */
